import React from 'react';
import { format } from 'date-fns';
import { TFunction } from 'i18next';

// Define the types for the data this component will receive
interface Order {
    id: number;
    date: string;
    status: string;
    clientName: string;
    shippingPrice: number;
    payment: string;
    payment_id?: string;
}

interface OrderDetailItem {
    name: string;
    cant: number;
    price: number;
}

interface EmailTemplateProps {
    orderId: number;
    order: Order;
    orderItems: OrderDetailItem[];
    formatter: Intl.NumberFormat;
    t: TFunction<'global'>;
}

// This is a "headless" component. It only returns HTML structure for the email.
const OrderEmailTemplate: React.FC<EmailTemplateProps> = ({ orderId, order, orderItems, formatter, t }) => {
    const subtotal = orderItems.reduce((acc, item) => acc + (item.price * item.cant), 0);
    const total = subtotal + (order.shippingPrice || 0);
    
    // --- THIS IS THE MISSING STYLES OBJECT ---
    const styles = {
        container: { fontFamily: 'Arial, sans-serif', fontSize: '14px', color: '#333333', lineHeight: '1.5' },
        header: { backgroundColor: '#f4f4f4', padding: '20px', textAlign: 'center' as const },
        h1: { margin: 0, color: '#111111' },
        h2: { color: '#111111' },
        h3: { borderBottom: '1px solid #eeeeee', paddingBottom: '10px', color: '#111111' },
        table: { width: '100%', borderCollapse: 'collapse' as const, marginTop: '20px' },
        th: { borderBottom: '2px solid #dddddd', padding: '10px 8px', textAlign: 'left' as const, color: '#555555' },
        td: { borderBottom: '1px solid #dddddd', padding: '10px 8px', textAlign: 'left' as const },
        totalsContainer: { marginTop: '30px', padding: '15px', backgroundColor: '#f9f9f9', textAlign: 'right' as const },
        paymentLinkContainer: { marginTop: '30px', padding: '20px', backgroundColor: '#fffbeb', border: '1px solid #e0b400', textAlign: 'center' as const },
        footer: { marginTop: '30px', paddingTop: '15px', borderTop: '1px solid #eeeeee', textAlign: 'center' as const, fontSize: '12px', color: '#888888' }
    };
    // --- END OF MISSING OBJECT ---

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1 style={styles.h1}>{t("email.thankYou")}</h1>
            </div>
            <div style={{ padding: '20px' }}>
                <h2 style={styles.h2}>{t('email.order')} #{orderId}</h2>
                <p><strong>{t('email.date')}:</strong> {format(new Date(order.date), 'MMM d, yyyy')}</p>
                <p><strong>{t('email.status')}:</strong> {order.status}</p>
                <p>{t('email.hello')} {order.clientName}, {t('email.summaryMessage')}</p>

                <h3 style={styles.h3}>{t('email.orderItems')}</h3>
                <table style={styles.table}>
                    <thead>
                        <tr>
                            <th style={styles.th}>{t('email.product')}</th>
                            <th style={styles.th}>{t('email.quantity')}</th>
                            <th style={styles.th}>{t('email.price')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {orderItems.map((item, index) => (
                            <tr key={index}>
                                <td style={styles.td}>{item.name}</td>
                                <td style={styles.td}>{item.cant}</td>
                                <td style={styles.td}>{formatter.format(item.price * item.cant)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <div style={styles.totalsContainer}>
                    <p style={{ margin: '5px 0' }}><strong>{t('email.subtotal')}:</strong> {formatter.format(subtotal)}</p>
                    <p style={{ margin: '5px 0' }}><strong>{t('email.shipping')}:</strong> {formatter.format(order.shippingPrice || 0)}</p>
                    <h3 style={{ margin: '10px 0' }}><strong>{t('email.total')}:</strong> {formatter.format(total)}</h3>
                </div>

                <h3 style={styles.h3}>{t('email.paymentDetails')}</h3>
                <p>
                    <strong>{t('email.paymentMethod')}:</strong>{' '}
                    {order.payment === 'toBeConvened' ? t('email.paymentToBeConvened') : order.payment}
                </p>
                {order.status === 'completed' && order.payment_id && (
                    <p>
                        <strong>{t('email.transactionId')}:</strong> {order.payment_id}
                    </p>
                )}

                {order.status === 'awaiting_payment' && (
                    <div style={styles.paymentLinkContainer}>
                        <p style={{ margin: '0 0 10px 0' }}>{t('email.pendingPaymentMessage')}</p>
                        <a href={`https://wicaglobalgear.com/orderDetail?orderId=${orderId}`} style={{color: '#007bff'}}>
                            {t('email.paymentLink')}
                        </a>
                    </div>
                )}

                <div style={styles.footer}>
                    <p>{t('email.shopWithUs')}</p>
                    <p><a href="https://wicaglobalgear.com" style={{color: '#007bff'}}>{t('email.visitWebsite')}</a></p>
                </div>
            </div>
        </div>
    );
};

export default OrderEmailTemplate;